/**
 * Dutch pack-size parsing.
 *
 * ADAPTERS §9.3 calls this load-bearing rather than cosmetic: the comparison
 * view computes price-per-litre from it, and a misparsed size makes a 500ml
 * carton look cheaper than a litre. One implementation, shared by every
 * adapter — not four.
 *
 * The governing rule is AI.md §8 applied to parsing: **null over guessing**.
 * Anything this function cannot read with confidence comes back null, and the
 * UI omits the unit price rather than showing a wrong one.
 *
 * Real strings observed in the AH `salesUnitSize` field during the ADAPTERS §7
 * reconnaissance: "500 g", "400 g", "2 stuks", "3 stuks", "1 l", "6 x 1 l".
 */
const UNIT_ALIASES = {
    ml: 'ml',
    milliliter: 'ml',
    cl: 'ml', // handled with a factor below
    l: 'l',
    lt: 'l',
    ltr: 'l',
    liter: 'l',
    g: 'g',
    gr: 'g',
    gram: 'g',
    kg: 'kg',
    kilo: 'kg',
    kilogram: 'kg',
    st: 'stuks',
    stuk: 'stuks',
    stuks: 'stuks',
    stk: 'stuks',
    x: 'stuks',
    pak: 'stuks',
    zakje: 'stuks',
    rol: 'stuks',
    rollen: 'stuks',
};
/** cl is the one alias whose quantity has to be scaled as well as renamed. */
const UNIT_FACTORS = { cl: 10 };
const TO_BASE = {
    ml: { factor: 0.001, base: 'l' },
    l: { factor: 1, base: 'l' },
    g: { factor: 0.001, base: 'kg' },
    kg: { factor: 1, base: 'kg' },
    stuks: { factor: 1, base: 'stuks' },
};
/** "1.5" and "1,5" both occur; Dutch decimal commas are the common case. */
function toNumber(raw) {
    const n = Number(raw.replace(',', '.'));
    return Number.isFinite(n) && n > 0 ? n : null;
}
function normalizeUnit(raw) {
    const key = raw.toLowerCase().replace(/\./g, '').trim();
    const unit = UNIT_ALIASES[key];
    if (!unit)
        return null;
    return { unit, factor: UNIT_FACTORS[key] ?? 1 };
}
function build(value, unit, pack) {
    const { factor, base } = TO_BASE[unit];
    return {
        value,
        unit,
        // Guard against float drift on values like 0.001 * 333.
        baseValue: Math.round(value * factor * 1e6) / 1e6,
        baseUnit: base,
        multipack: pack !== null,
        packCount: pack?.count ?? null,
        each: pack?.each ?? null,
    };
}
const MULTIPACK_RE = /^\s*(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*([a-z]+)\.?\s*$/i;
const SINGLE_RE = /^\s*(\d+(?:[.,]\d+)?)\s*([a-z]+)\.?\s*$/i;
const COUNT_ONLY_RE = /^\s*(\d+)\s*[x×]\s*$/i;
/**
 * How a size is written on screen: "1 l", "500 g", "6 stuks".
 *
 * Here rather than at each call site, because it was at three call sites and
 * one of them was wrong. `product_variants.size_value` is `numeric`, which
 * arrives from Postgres as the string "1.000" — interpolated raw, a one-litre
 * carton read as **1.000 l**, which in Dutch is one thousand litres. The server
 * happened to normalise it and the client's copy did not, so the wrong one was
 * the one on the list screen, which renders from the local mirror.
 *
 * This is exactly the divergence `shared/` exists to stop (see the
 * `finished`/`consumed` note in CLAUDE.md): one formatter, both sides.
 */
export function sizeLabel(value, unit) {
    if (value === null || value === undefined || !unit)
        return null;
    if (isSinglePiece(value, unit))
        return null;
    const parsed = parseSize(`${value} ${unit}`);
    // Falling back to the raw pair rather than to null: a size the parser cannot
    // read is still worth showing, it just cannot be normalised.
    if (!parsed)
        return `${value} ${unit}`;
    const shown = readable(parsed.value, parsed.unit);
    return `${dutchNumber(shown.value)} ${shown.unit}`;
}
/**
 * Whether a size is one countable piece — and therefore not a size at all.
 *
 * A shop writes `1 stuks` when there is nothing to write: one lettuce, one
 * cucumber, one bunch. Beside two other shops' rows for the same lettuce, which
 * state nothing, it reads as a distinguishing fact about this one — and the
 * unit price computed from it is the shelf price over again, one line lower and
 * in smaller type ("€2,59" / "€2,59/stuks"). Both are noise, and on a phone the
 * first one is noise that truncates the shop's name.
 *
 * Only exactly one: `2 stuks` is a real pack and `1 l` is a real size, and both
 * stay. A unit the parser cannot read stays too — it is left alone rather than
 * guessed at, the same as everywhere else here.
 */
export function isSinglePiece(value, unit) {
    if (value === null || value === undefined || !unit)
        return false;
    const parsed = parseSize(`${value} ${unit}`);
    return parsed !== null && parsed.baseUnit === 'stuks' && parsed.baseValue === 1;
}
/**
 * The unit a person would have used, given the number.
 *
 * The shops do not agree with each other and sometimes not with themselves: one
 * live page carried `2000 ml`, `7920 ml`, `1200 ml` and `0.5 l` at once. A
 * shopper comparing `2000 ml` against `2 l` has to do arithmetic to notice they
 * are the same size, and the one before that is the moment they conclude the
 * two rows are different products.
 *
 * Upward only, at a thousand: nothing is printed as a four-figure count of the
 * small unit. It deliberately does **not** go the other way — half a litre is
 * what the carton says, and rewriting `0,5 l` as `500 ml` would be this
 * function having an opinion about a size a shop stated plainly. Counts
 * (`stuks`) have nowhere to go and are left alone.
 */
function readable(value, unit) {
    if (unit === 'ml' && value >= 1000)
        return { value: round(value / 1000), unit: 'l' };
    if (unit === 'g' && value >= 1000)
        return { value: round(value / 1000), unit: 'kg' };
    return { value, unit };
}
/** Three decimals is a gram in a kilo; past that it is float noise, not a size. */
function round(n) {
    return Math.round(n * 1000) / 1000;
}
/**
 * `1,5 l`, not `1.5 l`. The app is in Dutch and a decimal point in a size is
 * the one place the translation had been forgotten — `String(n)` writes a JS
 * number, which is written the American way.
 */
function dutchNumber(n) {
    return String(n).replace('.', ',');
}
/**
 * Parse a pack-size string. Returns null when the string cannot be read with
 * confidence — that is a valid, expected outcome, not an error.
 */
export function parseSize(input) {
    if (!input)
        return null;
    const text = String(input).trim().toLowerCase();
    if (!text)
        return null;
    // "6 x 1 l", "2 x 500 ml" — the total is what a unit price must be based on.
    const multi = MULTIPACK_RE.exec(text);
    if (multi) {
        const count = toNumber(multi[1]);
        const each = toNumber(multi[2]);
        const norm = normalizeUnit(multi[3]);
        if (count === null || each === null || !norm)
            return null;
        const per = each * norm.factor;
        return build(count * per, norm.unit, {
            count,
            each: { value: per, unit: norm.unit },
        });
    }
    // "500 g", "1 l", "2 stuks"
    const single = SINGLE_RE.exec(text);
    if (single) {
        const value = toNumber(single[1]);
        const norm = normalizeUnit(single[2]);
        if (value === null || !norm)
            return null;
        return build(value * norm.factor, norm.unit, null);
    }
    // "4 x" — a bare count, seen on multipacks with no per-item size. A pack of
    // four somethings: the count is known and what one of them is, is not.
    const countOnly = COUNT_ONLY_RE.exec(text);
    if (countOnly) {
        const value = toNumber(countOnly[1]);
        if (value === null)
            return null;
        return build(value, 'stuks', { count: value, each: null });
    }
    return null;
}
/**
 * Split a parsed size back into the `size_value` / `size_unit` columns on
 * product_variants (DATABASE §5.2), which store the size as printed.
 */
export function toColumns(size) {
    if (!size)
        return { size_value: null, size_unit: null };
    return { size_value: size.value, size_unit: size.unit };
}
//# sourceMappingURL=units.js.map